import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  STAGE_ORDER,
  UNKNOWN_PARENT,
  formatWindow,
  type SituationDisposition,
} from "@mosaic/shared";
import { api } from "../api/client.js";
import { FilingRow } from "../components/FilingRow.js";
import { DispositionPanel } from "../components/DispositionPanel.js";
import { ExtractPanel } from "../components/ExtractPanel.js";
import { Financials } from "../components/Financials.js";
import { InsiderActivity } from "../components/InsiderActivity.js";
import { Businesses } from "../components/Businesses.js";
import { IndexMembershipValue } from "../components/IndexMembership.js";
import { MarketCapValue } from "../components/MarketCap.js";
import { FlagList } from "../components/FlagList.js";
import { TermsGrid } from "../components/TermsGrid.js";
import { Workspace } from "../components/Workspace.js";
import {
  DISPOSITION_LABEL,
  STAGE_LABEL,
  companyHandle,
  countdownLabel,
  identityLine,
  isNear,
  stageColorVar,
  whenLabel,
} from "../lib/format.js";

/** The reading surface for one situation: calm where the index column is dense. */
export function SituationPage() {
  const { id = "" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const situation = useQuery({ queryKey: ["situation", id], queryFn: () => api.situation(id) });
  const marketCap = useQuery({
    queryKey: ["marketcap", id],
    queryFn: () => api.situationMarketCap(id),
  });
  const indexes = useQuery({
    queryKey: ["indexes", id],
    queryFn: () => api.situationIndexes(id),
  });
  const facts = useQuery({ queryKey: ["facts", id], queryFn: () => api.facts(id) });
  const flags = useQuery({ queryKey: ["flags", id], queryFn: () => api.flags(id) });
  const filings = useQuery({ queryKey: ["filings", id], queryFn: () => api.situationFilings(id) });
  const timeline = useQuery({ queryKey: ["timeline", id], queryFn: () => api.timeline(id) });

  const dismiss = useMutation({
    mutationFn: (flagId: string) => api.dismissFlag(id, flagId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["flags", id] });
      // The row on the radar carries the same flags, so it has to be refetched too or
      // a dismissed finding stays visible in the list that led here.
      await queryClient.invalidateQueries({ queryKey: ["situations"] });
    },
  });

  const pin = useMutation({
    mutationFn: (isPinned: boolean) => api.patchSituation(id, { isPinned }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["situation", id] });
      await queryClient.invalidateQueries({ queryKey: ["situations"] });
    },
  });

  /**
   * "I am done with this", and nothing more than that: archiving hides the row and does
   * nothing else. The filings, facts and extraction runs stay, and reconciliation keeps
   * attaching new filings here, because a distribution is the middle of the interesting
   * window, not the end of it. "This was never a separation" is `disposition` below.
   */
  const archive = useMutation({
    mutationFn: (isArchived: boolean) => api.patchSituation(id, { isArchived }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["situation", id] });
      // The index column filters on `is_archived` server-side, so the row only
      // leaves — or comes back to — the radar once that list query refetches.
      await queryClient.invalidateQueries({ queryKey: ["situations"] });
    },
  });

  /**
   * The owner's judgement, and the only control here that changes what the pipeline
   * does: `not_a_situation` stops reconciliation matching new filings against this
   * situation, so a falsely surfaced company gets a new situation if it ever announces a
   * real separation. The other values do not: a separation the owner passed on is still a
   * real separation, and later filings about it belong here rather than on a duplicate.
   */
  const setDisposition = useMutation({
    mutationFn: (next: { disposition: SituationDisposition; dispositionReason: string | null }) =>
      api.patchSituation(id, next),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["situation", id] });
      // The radar filters on disposition server-side, so the row only leaves — or comes
      // back to — the list once that query refetches.
      await queryClient.invalidateQueries({ queryKey: ["situations"] });
      // The judgement is written to the timeline as an event, beside the filings that
      // produced the situation.
      await queryClient.invalidateQueries({ queryKey: ["timeline", id] });
    },
  });

  if (situation.isLoading) {
    return (
      <div className="read">
        <div className="empty">
          <p>Loading…</p>
        </div>
      </div>
    );
  }
  if (situation.isError || !situation.data) {
    return (
      <div className="read">
        <div className="empty">
          <p>That situation could not be loaded.</p>
          <p className="hint">{(situation.error as Error | undefined)?.message}</p>
        </div>
      </div>
    );
  }

  const s = situation.data;
  const days = s.daysToDistribution;
  const countdown = countdownLabel(days, s.stage);
  const stageColor = stageColorVar(s.stage);
  const askOpen = searchParams.get("ask") === "1";

  return (
    <div className="read">
      <div className="rtop">
        <div className="rtopin">
          <div className="crumb">
            <Link to="/">Radar</Link>
            {` › ${STAGE_LABEL[s.stage]} › ${companyHandle(s.parent)} / ${companyHandle(s.spinco)}`}
          </div>
          <div className="acts">
            {/*
              Ask opens as a panel of the shell rather than a section further down this
              page, and the state goes in the URL so the page's own button can drive a
              panel the layout renders — see `RadarLayout`.
            */}
            <button
              className="iconbtn"
              /*
                Named for what it does, not for what it says. With the panel open the
                page holds two controls reading "Ask" — this one and the panel's own
                submit — and a screen reader offered both has no way to tell them apart.
              */
              aria-label="Ask panel"
              aria-pressed={askOpen}
              onClick={() => {
                const next = new URLSearchParams(searchParams);
                if (askOpen) next.delete("ask");
                else next.set("ask", "1");
                setSearchParams(next, { replace: true });
              }}
            >
              Ask
            </button>
            <button
              className="iconbtn"
              aria-pressed={s.isPinned}
              onClick={() => pin.mutate(!s.isPinned)}
            >
              {s.isPinned ? "Pinned" : "Pin"}
            </button>
            <button
              className="iconbtn"
              aria-pressed={s.isArchived}
              onClick={() => archive.mutate(!s.isArchived)}
            >
              {s.isArchived ? "Unarchive" : "Archive"}
            </button>
          </div>
        </div>
      </div>

      <article className="article">
        <div className="eyebrow" style={{ ["--sc" as string]: stageColor }}>
          <i />
          <span>{STAGE_LABEL[s.stage]}</span>
          {days !== null ? (
            <span>· distribution {whenLabel(days, s.stage)}</span>
          ) : (
            s.expectedCompletion && (
              // "Expected" is doing real work here: everything else on this line is
              // derived from evidence a filing committed to, and this is a window it
              // gestured at.
              <span>· expected {formatWindow(s.expectedCompletion)}</span>
            )
          )}
        </div>

        {/* The SpinCo's own page, once it has a CIK — before the distribution it often
            does not, and the title stays plain text rather than a dead link. */}
        <h1>
          {s.spinco.cik ? (
            <Link className="h1link" to={`/companies/${s.spinco.cik}`}>
              {s.spinco.name}
            </Link>
          ) : (
            s.spinco.name
          )}
        </h1>
        <p className="deck">
          {s.parent.cik ? (
            /*
              A resolved parent links to its own page. The name is a link only when there
              IS a CIK behind it: a parent read out of prose and not yet matched has no
              page to open, and a link that went nowhere would claim Mosaic holds
              something it does not.
            */
            <>
              Separating from{" "}
              <Link to={`/companies/${s.parent.cik}`}>
                <strong>{s.parent.name}</strong>
              </Link>
              {s.parent.ticker ? ` (${s.parent.ticker})` : ""}.
            </>
          ) : s.parent.name !== UNKNOWN_PARENT ? (
            /*
              A parent read out of the document text, with no CIK behind it. A Form 10 is
              filed by the SpinCo, so the parent is in no filing header — the name is the
              only identification there is, and it is a complete answer to "spun out of
              what". Saying it is unmatched is the honest part: without a CIK, Mosaic is
              not fetching this company's filings or attaching them here.
            */
            <>
              Separating from <strong>{s.parent.name}</strong>, named in the filings but not
              yet matched to an EDGAR company.
            </>
          ) : (
            <>The parent is not identified in the filings ingested so far.</>
          )}
        </p>

        {/*
          Both sides' market capitalisation, above the terms rather than inside them: it
          is not a term of the separation, it is what the market currently says the two
          companies are worth. Each side names itself, because on a spin-off the
          interesting comparison is between them.
        */}
        <div className="mcrow">
          <span className="mcside">
            <span className="mclabel">Parent</span>
            <MarketCapValue result={marketCap.data?.parent} />
            <IndexMembershipValue result={indexes.data?.parent} />
          </span>
          <span className="mcside">
            <span className="mclabel">SpinCo</span>
            <MarketCapValue result={marketCap.data?.spinco} />
            <IndexMembershipValue result={indexes.data?.spinco} />
          </span>
        </div>

        <div className="byline">
          <span>
            <b>{identityLine(s)}</b>
          </span>
          <span>
            confidence <b>{s.confidence.toFixed(2)}</b>
          </span>
          {/*
            The dates the situation turns on, in the order they happen. No filing or fact
            counts: the filings section on this page says which they are, and a fact
            count is a number nobody acts on.
          */}
          {s.announcedOn && (
            <span>
              announced <b>{s.announcedOn}</b>
            </span>
          )}
          {s.recordDate && (
            <span>
              record <b>{s.recordDate}</b>
            </span>
          )}
          {s.distributionDate && (
            <span>
              distribution <b>{s.distributionDate}</b>
            </span>
          )}
          {/*
            The countdown, accented by the same `isNear` the radar row uses, so a
            situation does not read as imminent on one screen and ordinary on the next.
          */}
          {countdown && (
            <span className={isNear(s.daysToDistribution) ? "near" : undefined}>
              <b>{countdown}</b>
            </span>
          )}
        </div>

        {/*
          A dismissed situation is gone from the radar and nothing else here would say so.
          Stated where the reading starts, with the two things that are not obvious from
          the word: nothing was destroyed, and it is reversible.

          **The filter is named from the disposition rather than written out**, so the
          chip label, the radar filter and this word are one string and the direction
          cannot name the wrong chip.
        */}
        {s.disposition !== "unreviewed" && s.disposition !== "watching" && (
          <div className="notice">
            <b>{DISPOSITION_LABEL[s.disposition]}</b>
            {s.dispositionReason ? `${s.dispositionReason} — ` : ""}
            Hidden from the radar. Nothing was deleted; the{" "}
            {DISPOSITION_LABEL[s.disposition]} filter lists this situation, and
            Disposition below returns it.
            {/*
              Says "new situation", matching `DISPOSITION_BLURB.not_a_situation` on the
              panel below. The two sentences stay separate — the button states a
              consequence and this states a standing condition — but they describe one
              behaviour and must not describe it in two vocabularies. Both are pinned by
              tests.
            */}
            {s.disposition === "not_a_situation" &&
              " While it stands, a later filing by these companies opens a new situation rather than joining this one."}
          </div>
        )}

        {s.isArchived && (
          <div className="notice">
            <b>Archived</b>
            Off the radar — the filings, facts and extraction runs below are intact, and
            new filings still attach here. The radar's Archived filter brings it back.
          </div>
        )}

        {s.warnings.length > 0 && (
          <div className="notice">
            <b>
              {s.warnings.length} extraction warning{s.warnings.length === 1 ? "" : "s"}
            </b>
            Something did not parse cleanly here. The facts below may be incomplete.
            <ul>
              {s.warnings.slice(0, 5).map((w, i) => (
                <li key={i}>{w.message}</li>
              ))}
            </ul>
          </div>
        )}

        {facts.data && <Businesses situation={s} facts={facts.data.items} />}

        <div className="sechd">Terms</div>
        {facts.data ? (
          <TermsGrid facts={facts.data.items} />
        ) : (
          <p className="deck">Loading terms…</p>
        )}

        {/*
          The panel is told what the grid above it is showing, because otherwise it
          would offer to fill in terms that are already on screen — and the owner
          would reasonably read filled-in terms as a situation that had been read.
          The free pass produces `rule` facts; only a deep read produces `llm` ones.
        */}
        <ExtractPanel
          situationId={id}
          ruleDerivedTerms={(facts.data?.items ?? []).filter((f) => f.method === "rule").length}
        />

        <StageTrack stage={s.stage} />

        {/*
          Directly under the derived stage, because the pairing is the point: the track
          above is what the filings prove, this is what the owner concluded, and neither
          one is allowed to move the other.
        */}
        <DispositionPanel
          situation={s}
          pending={setDisposition.isPending}
          // The panel sends no reason: a judgement the owner makes by pressing a button
          // carries the button's own meaning.
          onSet={(disposition) => setDisposition.mutate({ disposition, dispositionReason: null })}
        />

        {flags.data && <FlagList flags={flags.data.items} onDismiss={(f) => dismiss.mutate(f)} />}

        <InsiderActivity situationId={id} />

        <Financials situationId={id} />

        <Workspace situationId={id} />

        {timeline.data && timeline.data.items.length > 0 && (
          <>
            <div className="sechd">Timeline</div>
            <div className="tl">
              {timeline.data.items.map((e) => (
                <div
                  key={e.id}
                  className={`tlrow${e.isExpected ? "" : " done"}`}
                  style={{ ["--sc" as string]: stageColor }}
                >
                  <div className="tld">{e.occurredOn}</div>
                  <div className="tlt">{e.title}</div>
                  {e.body && <div className="tlx">{e.body}</div>}
                  {e.accession && (
                    <div className="tlx">
                      <Link to={`/filings/${e.accession}`}>{e.accession}</Link>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {filings.data && filings.data.items.length > 0 && (
          <>
            <div className="sechd">Filings</div>
            <div className="filings">
              {filings.data.items.map((f) => (
                <FilingRow
                  key={f.accession}
                  {...f}
                  isPrimary={f.isPrimary ?? true}
                  changes={f.changes ?? []}
                />
              ))}
            </div>
          </>
        )}
      </article>
    </div>
  );
}

/**
 * Where the separation is in its arc. `abandoned` is a terminal off-ramp rather than
 * a step, so it replaces the track instead of appearing on it.
 */
function StageTrack({ stage }: { stage: string }) {
  if (stage === "abandoned") {
    return (
      <div className="notice">
        <b>Abandoned</b>
        This separation was called off, or converted to an outright sale.
      </div>
    );
  }
  const reached = STAGE_ORDER.indexOf(stage as (typeof STAGE_ORDER)[number]);
  return (
    <>
      <div className="sechd">Progress</div>
      <div className="tl">
        {STAGE_ORDER.map((s, i) => (
          <div
            key={s}
            className={`tlrow${i <= reached ? " done" : ""}`}
            style={{ ["--sc" as string]: stageColorVar(s) }}
          >
            <div className="tld">{i <= reached ? "reached" : "expected"}</div>
            <div className="tlt">{STAGE_LABEL[s]}</div>
          </div>
        ))}
      </div>
    </>
  );
}
